import { LayerKey, resolveTopologicalPath } from '@festina-life/api'
import { type PropsWithChildren, useMemo } from 'react'

import { useConfigurationContext } from '../context/configurationContext'
import { useMetaModelContext } from '../context/metaModelContext'
import { useNavigationParams } from '../system/useNavigationParams'
import { Provider } from './applicationViewOperationsContext'
import { enrichTemplateVersionInfos, generateModel } from './helpers'
import { useApplicationViewEnumDefinitions } from './useApplicationViewEnumDefinitions'
import { useApplicationViewGlMaps } from './useApplicationViewGlMaps'
import { useApplicationViewParameterDefinitions } from './useApplicationViewParameterDefinitions'
import { useApplicationViewParameterValues } from './useApplicationViewParameterValues'
import { useApplicationViewReconciliationSpecs } from './useApplicationViewReconciliationSpecs'
import { useApplicationViewRegisteredTenants } from './useApplicationViewRegisteredTenants'
import { useApplicationViewTemplateOperations } from './useApplicationViewTemplateOperations'

export const ApplicationViewOperationsProvider = ({
  children,
}: PropsWithChildren) => {
  const { block, layer } = useNavigationParams()
  const { viewAt } = useConfigurationContext()
  const { layerTopology } = useMetaModelContext()
  const applicationTemplates = useApplicationViewTemplateOperations()
  const applicationParameterDefinitions =
    useApplicationViewParameterDefinitions()
  const applicationEnumDefinitions = useApplicationViewEnumDefinitions()
  const applicationGlMaps = useApplicationViewGlMaps()
  const applicationParameterValues = useApplicationViewParameterValues()
  const applicationReconciliationSpecs = useApplicationViewReconciliationSpecs()
  const applicationRegisteredTenants = useApplicationViewRegisteredTenants()

  const applicationTemplateVersionInfos = useMemo(() => {
    const model = generateModel(applicationTemplates)
    const topologicalPath = resolveTopologicalPath(layer, layerTopology)

    const noTenantLayerKeys = topologicalPath.toReversed().slice(1) // The current layer should not be included

    // Lower keys in inverse topological order. Eg Customer > Country > base
    const lowerLayerKeys = layer.tenantId
      ? [
          new LayerKey({ layerId: layer.layerId, tenantId: null }),
          ...noTenantLayerKeys,
        ]
      : noTenantLayerKeys

    return enrichTemplateVersionInfos({
      block,
      model,
      viewDate: viewAt,
      layer,
      lowerLayers: lowerLayerKeys,
    })
  }, [viewAt, applicationTemplates, layer, layerTopology, block])

  const value = useMemo(
    () => ({
      applicationTemplateVersionInfos,
      applicationTemplates,
      applicationParameterDefinitions,
      applicationEnumDefinitions,
      applicationGlMaps,
      applicationParameterValues,
      applicationReconciliationSpecs,
      applicationRegisteredTenants,
    }),
    [
      applicationTemplateVersionInfos,
      applicationTemplates,
      applicationParameterDefinitions,
      applicationEnumDefinitions,
      applicationGlMaps,
      applicationParameterValues,
      applicationReconciliationSpecs,
      applicationRegisteredTenants,
    ],
  )

  return <Provider value={value}>{children}</Provider>
}
